import './lib/sentry';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';

import { PostHogProvider } from './components/PostHogProvider/PostHogProvider';
import { routeTree } from './routeTree.gen';

import '@archieai/ui/dist/ui.css';
import './index.css';

function ErrorFallback() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-2xl font-bold">Something went wrong</h1>
      <p className="text-gray-600">
        An unexpected error occurred. Please try reloading the page.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="rounded-lg bg-black px-6 py-3 text-white hover:bg-gray-800"
      >
        Reload page
      </button>
    </div>
  );
}

const router = createRouter({
  routeTree,
  defaultPendingMs: 100,
  defaultPendingMinMs: 300,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root')!;

// Clear any pre-rendered SSG content so the React SPA takes over.
// The static HTML inside #root is only meant for SEO crawlers that
// don't execute JavaScript; real users should always get the full SPA.
rootElement.innerHTML = '';

const root = createRoot(rootElement);
root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <PostHogProvider>
        <HelmetProvider>
          <RouterProvider router={router} />
        </HelmetProvider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
